import { z } from "zod";
import { completionNoteDate } from "./completion-note";
import { journalDecisionLines } from "./journal-decision";
import {
  journalFinalVersionSchema,
  journalWebdocStatusSchema,
} from "./journal-provenance";
import { JOURNAL_SECTION_MAX } from "./meeting-journal";
import { ordinationProse } from "./prescription-cycle";
import {
  confirmPrescriptionChoiceSchema,
  type ConfirmPrescriptionChoice,
} from "./treatment-start";

// TES-1080 (TES-1069 S5, points 1 and 10) — "Dokumentera receptutfärdandet".
//
// Emelie, TES-1069 point 1: one new draft per medical assessment, "containing only the new information".
// Earlier notes are readable references and never part of the next copy field.
// Regression test 12: "Tidigare journaltext följer inte med i nästa kopieringsfält."
//
// The note of the final transaction "Bekräfta recept och publicera behandlingen":
// the prescription choice, the drug, dose and frequency, and that the
// treatment plan is published.
//
// TES-1095 (Emelie, TES-1093 rule 8) — no subscription data in the journal:
// "Abonnemangsval, betalningsstatus, betalningslänk och annan kommersiell eller administrativ abonnemangsinformation ska inte ingå i journalanteckningen."
// The "Abonnemang" heading and its "Abonnemanget är aktiverat sedan …" line
// (TES-1080, Ö-318) are gone. Edits a doctor saved under the old key stay in
// journal_revisions and are simply no longer part of the note. Kept in
// journal_revisions with parentKind `prescription_note` and the pending
// first cycle's id (one note per confirmation), like TES-1060's
// Kompletteringsanteckning: every saved box is an `edit`, the copied text a
// `final`, and the "Införd i Webdoc" receipt a journal_webdoc_receipts row of
// the same kind.
//
// Deterministic and without AI (TES-1050). The facts are FIXED LINES that
// lead their box when the note is assembled and are never written into it,
// the TES-1058 rule: the doctor adds to a box, he does not retype or rewrite
// the facts the confirmation itself records. So the prescription line always
// says the choice the confirmation sends, and changing the choice changes the
// note (and takes the tick back, the TES-1063 pattern).
//
// Reused phrasing: "Receptstatus: …" is TES-1058's (journalDecisionLines),
// "Ordination: …" is TES-1019's (ordinationProse). The plan line is new with
// TES-1080.

export const PRESCRIPTION_NOTE_ACTION_LABEL = "Dokumentera receptutfärdandet";
export const PRESCRIPTION_NOTE_TITLE = "Receptanteckning";

export const PRESCRIPTION_NOTE_SECTION_KEYS = [
  "recept",
  "ordination",
  "behandlingsplan",
] as const;
export const prescriptionNoteSectionKeySchema = z.enum(
  PRESCRIPTION_NOTE_SECTION_KEYS,
);
export type PrescriptionNoteSectionKey = z.infer<
  typeof prescriptionNoteSectionKeySchema
>;

export const PRESCRIPTION_NOTE_HEADINGS: Record<
  PrescriptionNoteSectionKey,
  string
> = {
  recept: "Recept",
  ordination: "Ordination",
  behandlingsplan: "Behandlingsplan",
};

// A new line (TES-1080), not from Emelie's documents.
export const TREATMENT_PLAN_PUBLISHED_LINE =
  "Behandlingsplanen är publicerad för patienten.";

/** What the server has stored and the note states as fact. */
export interface PrescriptionNoteFacts {
  /** The planned order on the pending first cycle. */
  order: {
    medication: string | null;
    dose: string;
    frequency: string | null;
    duration: string | null;
  };
}

/**
 * The fixed lines of each box. The prescription line exists only once a
 * choice is made; without one the note cannot be copied (the server refuses
 * a finalize without it).
 */
export function prescriptionNoteFixedLines(
  facts: PrescriptionNoteFacts,
  prescription: ConfirmPrescriptionChoice | null | undefined,
): Record<PrescriptionNoteSectionKey, string[]> {
  const ordination = ordinationProse(facts.order);
  return {
    recept: prescription
      ? journalDecisionLines({
          prescription: {
            choice: prescription.choice,
            existingValidUntil: prescription.existingValidUntil ?? null,
          },
        }).plan
      : [],
    ordination: ordination ? [ordination] : [],
    behandlingsplan: [TREATMENT_PLAN_PUBLISHED_LINE],
  };
}

function normalize(text: string): string {
  return text
    .replace(/\r\n?/g, "\n")
    .replace(/[ \t]+$/gm, "")
    .trim();
}

/**
 * The text "Kopiera" copies: the dated title, then each heading with its
 * fixed lines and the doctor's own text. Nothing from an earlier note is
 * ever part of it. A fixed line the box already carries is not repeated.
 */
export function assemblePrescriptionNoteText(
  noteDate: string,
  facts: PrescriptionNoteFacts,
  sections: readonly { key: PrescriptionNoteSectionKey; content: string }[],
  prescription: ConfirmPrescriptionChoice | null | undefined,
): string {
  const fixed = prescriptionNoteFixedLines(facts, prescription);
  const byKey = new Map(sections.map((s) => [s.key, s.content]));
  const blocks: string[] = [];
  for (const key of PRESCRIPTION_NOTE_SECTION_KEYS) {
    const own = normalize(byKey.get(key) ?? "");
    const have = new Set(own.split("\n").map((l) => l.trim()));
    const lead = fixed[key].filter((l) => !have.has(l.trim()));
    const text = [...lead, own].filter(Boolean).join("\n");
    if (text) blocks.push(`${PRESCRIPTION_NOTE_HEADINGS[key]}\n${text}`);
  }
  return `${PRESCRIPTION_NOTE_TITLE} ${noteDate}\n\n${blocks.join("\n\n")}`;
}

/** The note's date as printed in its title: the Stockholm calendar day. */
export const prescriptionNoteDate = completionNoteDate;

// ---- The workspace -----------------------------------------------------------

export const prescriptionNoteSectionSchema = z.object({
  key: prescriptionNoteSectionKeySchema,
  heading: z.string(),
  // The doctor's own text under the heading (his newest edit), "" when none.
  content: z.string(),
});
export type PrescriptionNoteSection = z.infer<
  typeof prescriptionNoteSectionSchema
>;

// An earlier, finished note of the patient, shown as a read-only reference
// and never part of the copied text. `locked`: the API refuses edits to it
// (it is in Webdoc and a later note has been started).
export const prescriptionNoteEarlierNoteSchema = z.object({
  parentKind: z.enum([
    "meeting",
    "cycle",
    "transfer_review",
    "completion_note",
  ]),
  parentId: z.string(),
  // When its newest final version was copied.
  finalizedAt: z.string(),
  text: z.string(),
  webdoc: journalWebdocStatusSchema,
  locked: z.boolean(),
});
export type PrescriptionNoteEarlierNote = z.infer<
  typeof prescriptionNoteEarlierNoteSchema
>;

export const PRESCRIPTION_NOTE_EARLIER_KIND_LABEL: Record<
  PrescriptionNoteEarlierNote["parentKind"],
  string
> = {
  meeting: "Vårdmöte",
  cycle: "Uppföljning",
  transfer_review: "Receptövertag",
  completion_note: "Kompletteringsanteckning",
};

export const prescriptionNoteSchema = z.object({
  // The pending first cycle: the note's parent.
  cycleId: z.string(),
  // The note's date (its first version, or today before there is one).
  noteDate: z.string(),
  facts: z.object({
    order: z.object({
      medication: z.string().nullable(),
      dose: z.string(),
      frequency: z.string().nullable(),
      duration: z.string().nullable(),
    }),
  }),
  sections: z.array(prescriptionNoteSectionSchema),
  earlierNotes: z.array(prescriptionNoteEarlierNoteSchema),
  webdoc: journalWebdocStatusSchema,
});
export type PrescriptionNote = z.infer<typeof prescriptionNoteSchema>;

// PUT …/treatment-start/prescription-note — the boxes he changed.
export const updatePrescriptionNoteSchema = z.object({
  sections: z
    .array(
      z.object({
        key: prescriptionNoteSectionKeySchema,
        content: z.string().max(JOURNAL_SECTION_MAX),
      }),
    )
    .min(1)
    .max(PRESCRIPTION_NOTE_SECTION_KEYS.length),
});
export type UpdatePrescriptionNote = z.infer<
  typeof updatePrescriptionNoteSchema
>;

// POST …/prescription-note/finalize — the choice the text is compiled with.
export const finalizePrescriptionNoteSchema = z.object({
  prescription: confirmPrescriptionChoiceSchema,
});
export type FinalizePrescriptionNote = z.infer<
  typeof finalizePrescriptionNoteSchema
>;

export const prescriptionNoteFinalizedSchema = journalFinalVersionSchema.extend(
  {
    version: z.number().int().positive(),
    text: z.string(),
    webdoc: journalWebdocStatusSchema,
  },
);
export type PrescriptionNoteFinalized = z.infer<
  typeof prescriptionNoteFinalizedSchema
>;
